import ApiService from '@/lib/api';
import Banner from '@/components/Banner';
import Fold from '@/components/Fold';
import { GetStaticProps } from 'next';
import GridGallery from '@/components/GridGallery';
import MainLayout from '../../layouts/MainLayout';
import Testimonials from '@/components/Testimonials';
import { withSiteName } from '@/utils/pageTitle';

export const getStaticProps: GetStaticProps = async () => {
  const apiService = new ApiService();
  const [menuData, { footer }, seo] = await Promise.all([
    apiService.getMenuData(),
    apiService.getMiscData(),
    apiService.getStaticPageSeo('approach'),
  ]);
  return {
    props: {
      menuData,
      footer,
      seo: seo ?? null,
    },
    revalidate: 3600,
  };
};

const hardcodedData = {
  fold: {
    heading: 'How We Get Things Done',
    image: { hardCoded: '/img/approach-page/hero-bg.png' },
  },
  testimonials1: {
    cite: 'Foxbox operated as a startup within a large organization. They moved quickly, purposefully, and pushed the boundaries of how we deliver software. I highly recommend them.',
    author: '- PLAMEN PETROV, PH.D., VICE PRESIDENT, EXPONENTIAL TECHNOLOGIES, ANTHEM, INC.',
    image: '/img/approach-page/plamen-petrov.jpeg',
  },
  testimonials2: {
    cite: 'The staff-side software made an exponential difference in how we work. It’s so much easier! Words can’t explain how big of an impact this was for our organization.',
    author: '- SUSAN LOWITZ, DIRECTOR OF OPERATIONS, NSLC',
    image: '/img/approach-page/cite-photo.png',
  },
  banner2: {
    heading: 'Let’s Get Things Done, Together',
    image: '/img/approach-page/banner-bg.png',
    cta: {
      url: '/contact',
      text: 'Chat With Us',
    },
  },
  grid: {
    heading: 'The 7 Things That Enable Us to Build Great Products',
    items: [
      {
        id: 0,
        title: 'Hire the best people (that fit our culture)',
        content: 'We hire smart, opinionated, caring people that love to solve your problems.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/our-hiring-philosophy',
        },
      },
      {
        id: 1,
        title: '',
        content: '',
        type: 'image',
        image: '/img/approach-page/gallery-1.png',
        imageAlt: 'Two Foxbox team members reviewing work together on a laptop in the office lounge.',
        cta: {
          text: '',
          url: '',
        },
      },
      {
        id: 2,
        title: 'Flexible cross-functional product teams',
        content:
          'We assemble a product team of digital specialists to match your needs and integrate with your existing teams.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/how-we-structure-our-product-strategy-and-delivery-teams',
        },
      },
      {
        id: 3,
        title: 'Modern digital strategies',
        content: 'We get your validated product or prototype to market, quickly and purposefully.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/getting-digital-solutions-to-market',
        },
      },
      {
        id: 4,
        title: 'Human-centered Design, Agile, & Continuous Delivery',
        content: 'We combine Human-centered Design and Agile methodologies to find the right customer-market fit.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/build-better-digital-products-with-human-centered-design',
        },
      },
      {
        id: 5,
        title: 'Delight customers and measure success via KPIs',
        content: 'We believe every project should be case study worthy by delivering significant results and value.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/how-we-helped-home-chef-case-study',
        },
      },
      {
        id: 6,
        title: 'Executive visibility done right',
        content:
          'We provide executives with the right project status information, at the right time to allow them to make the right decisions.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/how-we-handle-executive-reporting',
        },
      },
      {
        id: 7,
        title: 'Long-term partnerships & support',
        content: '95% of our clients renew after the first phase of work.',
        type: 'text',
        cta: {
          text: 'Read More',
          url: '/blog/how-to-nail-agile-collaboration-and-build-better-products/',
        },
      },
      {
        id: 8,
        title: '',
        content: '',
        type: 'image',
        image: '/img/approach-page/gallery-2.png',
        imageAlt: 'A Foxbox team meeting in a glass-walled conference room, reviewing a product roadmap on screen.',
        cta: {
          text: '',
          url: '',
        },
      },
    ],
  },
};

export const ApproachPage = ({ menuData, footer, seo }) => {
  const fold = true;

  return (
    <MainLayout
      footer={footer}
      menuData={menuData}
      isHomepage
      bgWhite={true}
      ogUrl="https://foxbox.com/approach"
      metaTitle={seo?.title || withSiteName('Our Approach')}
      metaDescription={
        seo?.description ||
        'Discover how Foxbox Digital uses agile delivery, human-centered design, and cross-functional product teams to build digital products that get results.'
      }
    >
      {fold && <Fold heading={hardcodedData.fold.heading} bgImage={hardcodedData.fold.image} />}
      <Testimonials
        quoteline={hardcodedData.testimonials1.cite}
        author={hardcodedData.testimonials1.author}
        image={hardcodedData.testimonials1.image}
        mode="light"
      />
      <GridGallery heading={hardcodedData.grid.heading} items={hardcodedData.grid.items} />
      <Testimonials
        quoteline={hardcodedData.testimonials2.cite}
        author={hardcodedData.testimonials2.author}
        image={hardcodedData.testimonials2.image}
      />
      <Banner
        cta={hardcodedData.banner2.cta}
        background={`url(${hardcodedData.banner2.image})`}
        primaryText={hardcodedData.banner2.heading}
        overlay={true}
        mode="dark"
      />
    </MainLayout>
  );
};

export default ApproachPage;
